import { supabase } from "@/lib/supabase-client";
import { buildExpenseItemMetaData } from "@/lib/domain/lineItems";

export const payrollItemService = {
  async createPayrollItems(
    items: {
      client_id: string;
      time_card_id: string;
      amount?: number;
      description?: string;
      rate?: string;
      quantity?: number;
      unit?: string;
      worker?: { id?: string; first_name?: string; last_name?: string };
      shift_id?: string;
      project_id?: string;
      position_name?: string;
      start_time?: string | null;
      end_time?: string | null;
    }[],
  ): Promise<any[]> {
    // Prepare payroll items with links in meta_data (IDs only - names are enriched on fetch)
    const payrollItemsData = items.map((item) => ({
      client_id: item.client_id,
      meta_data: {
        amount: item.amount,
        description: item.description,
        rate: item.rate, // Formatted rate string: "$35.50/hr", "$600/day", or "0.8x base"
        quantity: item.quantity,
        unit: item.unit,
        worker: item.worker, // { id, first_name, last_name }
        shift_id: item.shift_id,
        project_id: item.project_id,
        position_name: item.position_name,
        start_time: item.start_time || null,
        end_time: item.end_time || null,
        status: "PENDING",
        links: {
          time_cards: [item.time_card_id],
        },
      },
    }));

    // Insert payroll_items
    const { data, error } = await supabase.from("payroll_items").insert(payrollItemsData).select();

    if (error) {
      throw new Error(`Failed to create payroll items: ${error.message}`);
    }

    return data;
  },

  async createPayrollItemsFromExpenses(
    items: {
      client_id: string;
      expense_id: string;
      amount?: number;
      description?: string;
      shift_id?: string;
      project_id?: string;
      user_id?: string;
      quantity?: number;
      unit?: string;
    }[],
  ): Promise<any[]> {
    const userIds = [...new Set(items.map((item) => item.user_id).filter(Boolean))] as string[];
    let userMap: Record<string, { id: string; first_name: string; last_name: string }> = {};
    if (userIds.length > 0) {
      const { data: users } = await supabase.from("users").select("id, first_name, last_name").in("id", userIds);
      if (users) {
        userMap = Object.fromEntries(
          users.map((u) => [u.id, { id: u.id, first_name: u.first_name, last_name: u.last_name }]),
        );
      }
    }

    // Prepare payroll items with links in meta_data
    const payrollItemsData = items.map((item) => ({
      client_id: item.client_id,
      meta_data: buildExpenseItemMetaData(item, userMap, { pending: true }),
    }));

    // Insert payroll_items
    const { data, error } = await supabase.from("payroll_items").insert(payrollItemsData).select();

    if (error) {
      throw new Error(`Failed to create payroll items from expenses: ${error.message}`);
    }

    return data;
  },

  async getPayrollItems(includeArchived = false) {
    const { data, error } = await supabase.rpc("get_payroll_items", { p_include_archived: includeArchived });
    if (error) throw new Error(`Failed to fetch payroll items: ${error.message}`);
    return (data as any[]) || [];
  },

  async getPayrollItemById(id: string) {
    const { data, error } = await supabase.from("payroll_items").select("*").eq("id", id).single();

    if (error) {
      throw new Error(`Failed to fetch payroll item: ${error.message}`);
    }

    return data;
  },

  async deletePayrollItemsByIds(ids: string[]): Promise<void> {
    const { error } = await supabase.from("payroll_items").delete().in("id", ids);

    if (error) {
      throw new Error(`Failed to delete payroll items: ${error.message}`);
    }
  },

  async saveRowPatches(patches: { id: string; [key: string]: any }[]): Promise<void> {
    // Check if any patches contain nested objects (like meta_data) that need merging
    const patchesWithNestedObjects = patches.filter((patch) =>
      Object.values(patch).some(
        (v) => typeof v === "object" && v !== null && !Array.isArray(v) && !(v instanceof Date),
      ),
    );

    // If we have nested objects, we need to fetch current values and merge
    if (patchesWithNestedObjects.length > 0) {
      const idsToFetch = patchesWithNestedObjects.map((p) => p.id);
      const { data: currentRows, error: fetchError } = await supabase
        .from("payroll_items")
        .select("id, meta_data")
        .in("id", idsToFetch);

      if (fetchError) {
        throw new Error(`Failed to fetch current values: ${fetchError.message}`);
      }

      const currentDataMap = new Map(currentRows?.map((row) => [row.id, row]) || []);

      // Merge nested objects with current values
      for (const patch of patchesWithNestedObjects) {
        const currentRow = currentDataMap.get(patch.id);
        if (currentRow && patch.meta_data) {
          patch.meta_data = { ...(currentRow.meta_data || {}), ...patch.meta_data };
        }
      }
    }

    const promises = patches.map(({ id, ...fields }) => supabase.from("payroll_items").update(fields).eq("id", id));

    const results = await Promise.all(promises);
    const errors = results.filter((result) => result.error);

    if (errors.length > 0) {
      console.error(
        "Payroll item update errors:",
        errors.map((e) => e.error),
      );
      const firstError = errors[0]?.error;
      throw new Error(`Failed to update ${errors.length} payroll item(s): ${firstError?.message || "Unknown error"}`);
    }
  },

  async updatePayrollItemStatus(ids: string[], status: string): Promise<void> {
    // Fetch current meta_data for each item
    const { data: currentRows, error: fetchError } = await supabase
      .from("payroll_items")
      .select("id, meta_data")
      .in("id", ids);

    if (fetchError) {
      throw new Error(`Failed to fetch payroll items: ${fetchError.message}`);
    }

    // Merge status into meta_data and update each row
    const promises = (currentRows || []).map((row) =>
      supabase
        .from("payroll_items")
        .update({ meta_data: { ...(row.meta_data || {}), status } })
        .eq("id", row.id),
    );

    const results = await Promise.all(promises);
    const errors = results.filter((r) => r.error);

    if (errors.length > 0) {
      throw new Error(`Failed to update status for ${errors.length} item(s): ${errors[0]?.error?.message}`);
    }
  },

  async duplicatePayrollItem(id: string): Promise<any> {
    // Get the original payroll item
    const { data: original, error: fetchError } = await supabase
      .from("payroll_items")
      .select("*")
      .eq("id", id)
      .single();

    if (fetchError || !original) {
      throw new Error(`Failed to fetch payroll item: ${fetchError?.message || "Not found"}`);
    }

    // Create a duplicate with the same data (excluding id, created_at, updated_at)
    const duplicate = {
      client_id: original.client_id,
      meta_data: original.meta_data,
    };

    const { data, error } = await supabase.from("payroll_items").insert([duplicate]).select().single();

    if (error) {
      throw new Error(`Failed to duplicate payroll item: ${error.message}`);
    }

    return data;
  },
};
